import React, { useState, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "./Breadcrumb";
import { Button, Form, ListGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import { API_URL } from "..";

// Set your Mapbox access token
mapboxgl.accessToken = "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

const containerStyle = { 
  width: "75%", 
  height: "600px",
  position: "relative" 
};

const sidebarStyle = { 
  width: "25%", 
  height: "600px", 
  overflowY: "auto", 
  padding: "10px", 
  background: "#f8f9fa", 
  borderLeft: "2px solid #ddd" 
};

const DriverDashboard = () => {
  const [drivers, setDrivers] = useState([]);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const markers = useRef([]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      const user = localStorage.getItem('user');
      const value = JSON.parse(user);
      try {
        const response = await fetch(
          API_URL + '/api/truck/truck-with-location/' + value?.id,
        );
        const data = await response.json();
        console.log(data)
        const formattedDrivers = data.data.map((truck) => ({
          id: truck.truck_id,
          name: truck.driver.name,
          phone: truck.driver.phone,
          status: truck.assigned_jobs[0]?.status_for_job || "Waiting", // Default to "Waiting" if no job
          location: {
            lat: truck.location.latitude,
            lng: truck.location.longitude,
          },
        }));
        setDrivers(formattedDrivers);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [0, 0], // Default center
        zoom: 1 // Default zoom
      });
      map.current.addControl(new mapboxgl.NavigationControl());
    }

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, []);
  const getAddressFromCoordinates = async (lat, lng) => {
    // Ensure Mapbox is initialized
    if (!mapboxgl.accessToken) {
      throw new Error('Mapbox access token not configured');
    }
  
    // Parse coordinates
    let latitude, longitude;
    
    if (typeof lat === 'string' && lat.includes(',')) {
      // Handle "lat,lng" string format
      const coords = lat.split(',').map(coord => parseFloat(coord.trim()));
      if (coords.length !== 2 || coords.some(isNaN)) {
        throw new Error('Invalid coordinate format. Expected "latitude,longitude"');
      }
      [latitude, longitude] = coords;
    } else {
      // Handle separate lat/lng parameters
      latitude = parseFloat(lat);
      longitude = parseFloat(lng);
      if (isNaN(latitude) || isNaN(longitude)) {
        throw new Error('Invalid latitude or longitude values');
      }
    }
  
    // Validate coordinate ranges
    if (latitude < -90 || latitude > 90) {
      throw new Error('Latitude must be between -90 and 90 degrees');
    }
    if (longitude < -180 || longitude > 180) {
      throw new Error('Longitude must be between -180 and 180 degrees');
    }
  
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?` +
        `access_token=${mapboxgl.accessToken}` +
        `&types=address,place,poi,neighborhood,locality` +
        `&language=en&limit=1`
      );
  
      if (!response.ok) {
        throw new Error(`Mapbox API request failed with status ${response.status}`);
      }
  
      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        return 'Address not found';
      }
  
      // Return the most relevant address result
      return data.features[0].place_name;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      throw new Error(`Failed to get address: ${error.message}`);
    }
  };

  // Update markers and fit bounds when drivers change
  useEffect(() => {
    if (!map.current || !drivers.length) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    const bounds = new mapboxgl.LngLatBounds();

    drivers.forEach(driver => {
      const lngLat = [driver.location.lng, driver.location.lat];
      bounds.extend(lngLat);

      // Create marker element
      const el = document.createElement('div');
      el.className = 'truck-marker';
      el.style.backgroundImage = 'url(./assets/images/truck-icon.png)';
      el.style.width = '30px';
      el.style.height = '75px';
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat= 'no-repeat';
      el.style.cursor = 'pointer';

      // Create marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat(lngLat)
        .addTo(map.current);

      // Add click event
      marker.getElement().addEventListener('click', () => {
        onMarkerClick(driver);
      });

      markers.current.push(marker);
    });

    // Fit map to bounds
    map.current.fitBounds(bounds, {
      padding: 50,
      maxZoom: 12
    });
  }, [map.current, drivers]);

  const onMarkerClick = (driver) => {
    setSelectedDriver(driver);
    const fetchAddress = async () => {
      try {
        
        const addr = await getAddressFromCoordinates(
          driver.location.lat, 
          driver.location.lng
        );
        console.log(addr)
        setSelectedDriver((prev)=>({...prev, address: addr}))
        // setError(null);
      } catch (err) {
        // setError(err.message);
        setSelectedDriver((prev)=>({...prev, address: 'Nothing Found'}))
      }
    };
    fetchAddress();
  };

  const onDriverClick = (driver) => {
    setSelectedDriver(driver);
    if (map.current) {
      map.current.flyTo({
        center: [driver.location.lng, driver.location.lat],
        zoom: 10
      });
    }
  };

  const filteredDrivers = drivers.filter(
    (driver) =>
      driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.phone.includes(searchQuery) ||
      driver.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <MasterLayout>
      <Breadcrumb title="Driver Dashboard" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 d-flex">
          {/* Sidebar (Right Side - 25% width) */}
          <div style={sidebarStyle}>
            <h5 className="mb-3">Search Drivers</h5>
            <Form.Control
              type="text"
              placeholder="Search by name, phone, status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <h6 className="mt-3">Driver List</h6>
            <ListGroup>
              {filteredDrivers.length > 0 ? (
                filteredDrivers.map((driver) => (
                  <ListGroup.Item
                    key={driver.id}
                    action
                    onClick={() => onDriverClick(driver)}
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <div>
                      <strong>{driver.name}</strong>
                      <p style={{ fontSize: 12, margin: 0 }}>📞 {driver.phone}</p>
                    </div>
                  </ListGroup.Item>
                ))
              ) : (
                <p className="text-muted text-center mt-2">No drivers found</p>
              )}
            </ListGroup>
          </div>

          {/* Mapbox Map Section (Left Side - 75% width) */}
          <div style={containerStyle}>
            <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
            
            {/* Custom Info Window */}
            {selectedDriver && (
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  backgroundColor: "white",
                  borderRadius: "10px",
                  minWidth: "200px",
                  padding: "10px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  zIndex: 1
                }}
              >
                <button
                  onClick={() => setSelectedDriver(null)}
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px"
                  }}
                >
                  ×
                </button>
                <p style={{ fontSize: 18, fontWeight: "bold" }}>{selectedDriver.name}</p>
                <p style={{ fontSize: 14 }}>📞 <strong>Phone:</strong> {selectedDriver.phone}</p>
                <p style={{ fontSize: 14 }}>📍 <strong>Location:</strong> {selectedDriver?.address || selectedDriver.location.lat+','+selectedDriver.location.lng}</p>
                
                <Link 
                  to={'/assign-job-to-driver'} 
                  state={{label: selectedDriver.name, value: selectedDriver.id}} 
                  className="btn btn-info"
                >
                  Assign Job
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default DriverDashboard;